import { HttpResponse } from "owp.http";
import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Auth } from "@gss/common/Auth";
import { numberInt } from "@gss/common/numberInt";
import { getRegionOrgId } from "@gss/common/OrgId";
import { resultsPaths as paths } from "@gss/common/Paths";
import { Roles } from "@gss/common/Roles";
import { Alert } from "@gss/components/Alert";
import { InputCheckboxLabel } from "@gss/components/InputCheckboxLabel";
import { useMessages } from "@gss/components/Messages";
import { Select } from "@gss/components/Select";
import { useNavigate } from "@gss/hooks/useNavigate";
import { useParams } from "@gss/hooks/useParams";
import type { FilterOption, MeasurementStat } from "@gss/types/measurementStat";
import type { Month } from "@gss/types/Month";
import { isMonth } from "@gss/types/Month";
import type { SecurityContext } from "@gss/types/SecurityContext";
import { DAO } from "./DAO";
import { DownloadResultsTab } from "./download/DownloadResultsTab";
import { FreeTexTab as FreeTextTab } from "./FreeTextTab";
import { ShowResultsTab } from "./resultGraphs/ShowResultsTab";
import { SuperGraphTab } from "./resultGraphs/SuperGraphTab";
import type {
    MeasurementStatWithLockedFilters,
    StatsPeriods,
    UserOrganisations,
} from "./types";
import { Back } from "./util/Back";
import type { ResultTab } from "./util/ResultTabs";
import { ResultTabs } from "./util/ResultTabs";
import { Title } from "./util/Title";
import { useTranslation } from "./util/Translation";

interface MeasurementYear {
    value: string;
    months: Month[];
}

export function Measurement(): JSX.Element {
    const [audience, setAudience] = useState<SecurityContext>(
        Auth.isReader() ? "authenticated" : "public",
    );
    const [stat, setStat] = useState<MeasurementStatWithLockedFilters>();
    const [requireLogin, setRequireLogin] = useState(false);
    const [years, setYears] = useState<MeasurementYear[]>();
    const [userOrganistions, setUserOrganistions] =
        useState<UserOrganisations>();
    const [showIsPublicSelection, setShowIsPublicSelection] = useState(false);
    const navigate = useNavigate();
    const { addError } = useMessages();
    const { translation, translateFilter, translateMonth } = useTranslation();
    const params = useParams();
    const paramName = decodeURIComponent(params.get("name"));
    const paramPeriod = params.opt("period");
    const t = translation.measurement;

    const catchError = (error: unknown) => {
        const fullName = [paramName, paramPeriod].filter(Boolean).join(" ");
        addError({
            message: translation.error.loadMeasurement(fullName),
            cause: error,
        });
    };

    const load = (paramPeriod: string) => {
        Promise.all([
            DAO.stats.getMeasurementStat(audience, paramName, paramPeriod),
            DAO.stats.getPeriods(audience, paramName),
            Auth.hasRole(Roles.ORG_MANAGER) &&
            !Auth.hasRole(Roles.CONTACT_PERSON)
                ? DAO.stats.getManagerOrganisations(paramName, paramPeriod)
                : Promise.resolve([]),
            Auth.hasRole(Roles.CONTACT_PERSON) && audience === "authenticated"
                ? DAO.stats.getContactOrganisations()
                : Promise.resolve([]),
            // Always check if this is public, since contact persons can access both views
            Auth.hasRole(Roles.CONTACT_PERSON)
                ? DAO.stats.isPublished("public", paramName, paramPeriod)
                : Promise.resolve(false),
        ])
            .then(
                ([
                    stat,
                    periods,
                    orgManagerRoles,
                    contactPersonRoles,
                    isPublic,
                ]) => {
                    addPeriodsFilter(stat, periods.periods, paramPeriod);
                    setStat(getStatWithLockedFilters(stat));
                    setYears(getYears(periods));
                    setUserOrganistions(
                        getUserOrgRoles(orgManagerRoles, contactPersonRoles),
                    );
                    setShowIsPublicSelection(isPublic);
                },
            )
            .catch((error: unknown) => {
                if (isErrorLoginRequired(error)) {
                    setRequireLogin(true);
                } else {
                    catchError(error);
                }
            });
    };

    useEffect(() => {
        if (paramPeriod != null) {
            load(paramPeriod);
        } else {
            DAO.stats
                .getPeriods(audience, paramName)
                .then((periods) => {
                    const year = periods.periods[0].slice(0, 4);
                    navigate(year, { replace: true });
                })
                .catch((error: unknown) => {
                    if (isErrorLoginRequired(error)) {
                        setRequireLogin(true);
                    } else {
                        catchError(error);
                    }
                });
        }
    }, [audience, paramPeriod]);

    if (requireLogin) {
        return (
            <>
                <Back />
                <h1>{t.requireLogin}</h1>
            </>
        );
    }

    if (stat == null || years == null || userOrganistions == null) {
        return <Back />;
    }

    const renderTopRow = () => (
        <>
            <Back />

            {showIsPublicSelection && (
                <InputCheckboxLabel
                    className="float-end"
                    checked={audience === "public"}
                    onChange={(checked) => {
                        // Hide current while loading
                        setStat(undefined);
                        setAudience(checked ? "public" : "authenticated");
                    }}
                >
                    {t.publicView}
                </InputCheckboxLabel>
            )}
        </>
    );

    const renderMeasurementName = () => {
        const lockedFilters = Object.values(stat.lockedFilters).map((f) =>
            translateFilter(f.name),
        );
        return (
            <h1>
                {stat.measurement}
                {lockedFilters.length > 0 ? (
                    <small> ({lockedFilters.join(", ")})</small>
                ) : undefined}
            </h1>
        );
    };

    const renderYear = (year: MeasurementYear) => {
        const months = (() => {
            const months = year.months;
            if (months.length === 1) {
                return translateMonth(months[0]);
            }
            return `${translateMonth(months[0])} - ${translateMonth(months[months.length - 1])}`;
        })();
        return `${year.value} (${months})`;
    };

    const renderYearSelection = () => {
        if (years.length === 1) {
            return (
                <h2 className="h3">
                    {t.measurementYear}
                    &nbsp;
                    {renderYear(years[0])}
                </h2>
            );
        }
        return (
            <h2 className="h3">
                {t.measurementYear}
                <Select
                    className="d-inline-block w-auto ms-2 mb-2"
                    value={stat.periods}
                    aria-label={stat.periods}
                    onChange={(value) => {
                        // Hide current while loading
                        setStat(undefined);
                        navigate(`/${encodeURIComponent(paramName)}/${value}`);
                    }}
                >
                    {years.map((year) => ({
                        value: year.value,
                        children: renderYear(year),
                    }))}
                </Select>
            </h2>
        );
    };

    const renderInterpretationWarning = () => {
        const lines: string[] = [];
        if (stat.hasOptionalSelection && audience === "authenticated") {
            lines.push(translation.measurement.interpretationOptionalSelection);
        }
        lines.push(translation.measurement.interpretationVariedComposition);
        return (
            <Alert variant="primary" className="py-2 mb-2">
                <span className="fw-bolder">
                    {translation.measurement.interpretationNoteThat}:
                </span>
                <ul className="fst-italic m-0">
                    {lines.map((line, index) => (
                        <li key={index}>{line}</li>
                    ))}
                </ul>
            </Alert>
        );
    };

    const renderTabs = () => {
        const tabs: ResultTab[] = [
            {
                path: undefined,
                element: (
                    <ShowResultsTab
                        stat={stat}
                        audience={audience}
                        managerOrganistions={userOrganistions.manager}
                    />
                ),
                text: t.showResults,
            },
            {
                path: "laddaned",
                element: (
                    <DownloadResultsTab
                        stat={stat}
                        audience={audience}
                        userOrganistions={userOrganistions}
                    />
                ),
                text: t.downloadResults,
            },
        ];

        if (stat.orgStructures.some((o) => o.organisations.length > 1)) {
            tabs.push({
                path: "supergraf",
                element: (
                    <SuperGraphTab
                        stat={stat}
                        audience={audience}
                        regionRoles={userOrganistions.region}
                    />
                ),
                text: t.superGraph,
            });
        }

        if (
            Auth.hasRole(Roles.ORG_MANAGER) &&
            audience === "authenticated" &&
            stat.questions.some((q) => q.choiceFreeText)
        ) {
            tabs.push({
                path: "fritext",
                element: <FreeTextTab stat={stat} />,
                text: t.freeTextResponses,
            });
        }

        return (
            <ResultTabs
                basePath={paths.getMeasurementPath(
                    stat.measurement,
                    stat.periods,
                )}
                activePath={params.opt("tab")}
                tabs={tabs}
            />
        );
    };

    const fullName = `${stat.measurement} ${stat.periods}`;

    return (
        <div key={fullName + audience}>
            {renderTopRow()}

            <Title>{fullName}</Title>

            {renderMeasurementName()}
            {renderYearSelection()}
            {renderInterpretationWarning()}
            {renderTabs()}
        </div>
    );
}

function isErrorLoginRequired(error: unknown): boolean {
    if (error instanceof HttpResponse) {
        try {
            const data = error.json<{
                requireLogin?: boolean;
            }>();
            if (data.requireLogin) {
                return true;
            }
        } catch {
            // Do nothing
        }
    }
    return false;
}

function addPeriodsFilter(
    stat: MeasurementStat,
    periods: string[],
    period: string,
): void {
    if (stat.filters.some((f) => f.value === "periods")) {
        return;
    }

    const filteredPeriods = periods.filter(
        (p) => p.startsWith(period) || p <= period,
    );

    if (filteredPeriods.length < 2) {
        return;
    }

    const year = period.slice(0, 4);

    stat.filters.unshift({
        value: "periods",
        name: "Jämförelseperiod",
        description: "Tidsperioder att jämföra med",
        required: true,
        options: filteredPeriods.map((value) => ({
            name: value,
            value,
            selected: value === period,
        })),
        isOptionDisabled: (selectedValues: string[], value: string) => {
            // This option is disabled if: it includes the current year and is selected and no
            // other options fit the same criteria.
            // ie we must always have one selected option for the current year.
            return (
                value.includes(year) &&
                selectedValues.includes(value) &&
                !selectedValues.some((s) => s.includes(year) && s !== value)
            );
        },
    });
}

function getStatWithLockedFilters(
    stat: MeasurementStat,
): MeasurementStatWithLockedFilters {
    const lockedFilters: Record<string, FilterOption> = {};
    for (const filter of stat.filters) {
        // Locked filter. Just extract default values.
        if (filter.lockedOption != null) {
            const option = filter.options?.find(
                (o) => o.value === filter.lockedOption,
            );
            if (option == null) {
                throw new Error(
                    `Can't find locked option '${filter.lockedOption}'`,
                );
            }
            lockedFilters[filter.value] = option;
        }
    }
    const filtersBasedOnQuestion = new Set(
        stat.filters.filter((f) => f.basedOnQuestion).map((f) => f.value),
    );
    return {
        ...stat,
        lockedFilters,
        filtersBasedOnQuestion,
    };
}

function getYears(periods: StatsPeriods): MeasurementYear[] {
    const res: MeasurementYear[] = [];
    const used = new Set<string>();
    for (const p of periods.periods) {
        const year = p.slice(0, 4);
        if (!used.has(year)) {
            used.add(year);
            const monthsInYear = periods.months
                .filter((m) => m.startsWith(year))
                .toSorted();
            res.push({
                value: year,
                months: monthsInYear.map(yearMonthToMonth),
            });
        }
    }
    return res;
}

function getUserOrgRoles(
    managerRoles: string[],
    contactRoles: string[],
): UserOrganisations {
    const regionRoles = [...contactRoles];
    for (const role of managerRoles) {
        const regionRole = getRegionOrgId(role);
        if (!regionRoles.includes(regionRole)) {
            regionRoles.push(regionRole);
        }
    }
    return {
        contact: contactRoles,
        manager: managerRoles,
        region: regionRoles,
    };
}

function yearMonthToMonth(yearMonth: string): Month {
    const value = numberInt(yearMonth.slice(5)).toString();
    if (isMonth(value)) {
        return value;
    }
    throw new Error(`Invalid month: ${value}`);
}
